import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { href: "#whats-inside", label: "What's Inside" },
  { href: "#benefits", label: "Benefits" },
  { href: "#doctor", label: "Doctor" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#faq", label: "FAQ" },
];

export default function Navbar({ onCTAClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <motion.nav
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between px-4 sm:px-6 py-3 rounded-full transition-all ${
          scrolled ? "glass shadow-lg" : "bg-white/0"
        }`}>
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <span className="text-white font-black text-lg" style={{ fontFamily: "Outfit" }}>B</span>
            </div>
            <span className="font-black text-base sm:text-lg text-[#1E3A8A] hidden xs:inline" style={{ fontFamily: "Outfit" }}>
              BackCare
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-semibold text-slate-700 hover:text-[#1E3A8A] transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={onCTAClick}
              className="hidden sm:flex h-10 px-5 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold cta-glow"
              data-testid="nav-cta-button"
            >
              Get PDF ₹199
            </Button>
            <button
              onClick={() => setOpen(!open)}
              className="md:hidden w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-2 glass rounded-2xl p-4 shadow-lg"
          >
            <div className="flex flex-col gap-2">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <Button
                onClick={() => { setOpen(false); onCTAClick(); }}
                className="mt-2 h-12 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
                data-testid="mobile-nav-cta"
              >
                Get PDF ₹199
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}
